import { faBuilding } from "@fortawesome/pro-duotone-svg-icons"
import { faChevronLeft, faChevronRight, faTimes } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Checkbox, Form, Input, Select, Typography } from "antd"
import { useState, useEffect } from "react"
import { Geocoder } from '@mapbox/search-js-react'
import { useAccountSetup } from "./AccountSetupContext"
import { getAll as getAllCountries } from "../../services/countriesService"
import { getForCountry } from "../../services/statesService"
import { useCreateTenantLocationMutation, useUpdateTenantLocationMutation, useTenantLocationsDataQuery } from "../../domain/tenantLocations/tenantLocationsQueries"
import { useUpdateGeneralSettingsMutation, useInvoicingSettingsQuery } from "../../domain/invoicing/settings/invoicingSettingsQueries"
import { CreateUpdateTenantLocationRequest } from "../../domain/tenantLocations/tenantLocation"
import './AccountSetupModal.css'

const { Title, Text } = Typography

export type Props = {
  onBack: () => void
  onExit: () => void
}

const AccountSetupAddressStep = ({ onBack, onExit }: Props) => {
  const [shopForm] = Form.useForm()
  const [billingForm] = Form.useForm()

  const { state, dispatch, tenantData, saveTenantMutation } = useAccountSetup()
  const { businessDetails } = state

  // State for country/state dropdowns
  const [shopStateOptions, setShopStateOptions] = useState(buildStateOptions(businessDetails.shopAddress.countryCode))
  const [shopStateLabel, setShopStateLabel] = useState(getStateLabel(businessDetails.shopAddress.countryCode))
  const [billingStateOptions, setBillingStateOptions] = useState(buildStateOptions(businessDetails.billingAddress.countryCode))
  const [billingStateLabel, setBillingStateLabel] = useState(getStateLabel(businessDetails.billingAddress.countryCode))

  // API hooks
  const { data: existingLocations } = useTenantLocationsDataQuery()
  const { data: currentInvoicingSettings } = useInvoicingSettingsQuery()
  const createTenantLocation = useCreateTenantLocationMutation()
  const updateTenantLocation = useUpdateTenantLocationMutation()
  const updateGeneralSettings = useUpdateGeneralSettingsMutation()

  const isSaving = createTenantLocation.isLoading || updateTenantLocation.isLoading || updateGeneralSettings.isLoading || saveTenantMutation.isLoading

  // Initialize forms with state values
  useEffect(() => {
    shopForm.setFieldsValue(businessDetails.shopAddress)
    billingForm.setFieldsValue(businessDetails.billingAddress)
  }, [])

  // Prefill from existing data (only if reducer state is empty)
  useEffect(() => {
    const shopAddressIsEmpty = !businessDetails.shopAddress.address1

    if (shopAddressIsEmpty && existingLocations) {
      const existingShop = existingLocations.find(loc => loc.type === 0 && loc.active)
      if (existingShop) {
        // Populate shop address in reducer state
        dispatch({
          type: 'UPDATE_SHOP_ADDRESS',
          payload: {
            address1: existingShop.address1,
            city: existingShop.city,
            state: existingShop.stateCode,
            zip: existingShop.zip,
            countryCode: existingShop.countryCode,
            county: existingShop.county,
            latitude: existingShop.latitude,
            longitude: existingShop.longitude,
          }
        })

        // Update Ant Design form
        shopForm.setFieldsValue({
          address1: existingShop.address1,
          city: existingShop.city,
          state: existingShop.stateCode,
          zip: existingShop.zip,
          countryCode: existingShop.countryCode
        })

        // Update state dropdown options
        setShopStateOptions(buildStateOptions(existingShop.countryCode))
        setShopStateLabel(getStateLabel(existingShop.countryCode))
      }
    }

    const billingAddressIsEmpty = !businessDetails.billingAddress.address1
    const invoicingAddress = currentInvoicingSettings?.invoicingAddress

    if (billingAddressIsEmpty && invoicingAddress?.address1) {
      // Populate billing address in reducer state
      dispatch({
        type: 'UPDATE_BILLING_ADDRESS',
        payload: {
          address1: invoicingAddress.address1,
          city: invoicingAddress.city,
          state: invoicingAddress.state,
          zip: invoicingAddress.zip,
          countryCode: invoicingAddress.countryCode,
        }
      })

      // Update Ant Design form
      billingForm.setFieldsValue({
        address1: invoicingAddress.address1,
        city: invoicingAddress.city,
        state: invoicingAddress.state,
        zip: invoicingAddress.zip,
        countryCode: invoicingAddress.countryCode
      })

      // Update state dropdown options
      setBillingStateOptions(buildStateOptions(invoicingAddress.countryCode))
      setBillingStateLabel(getStateLabel(invoicingAddress.countryCode))

      // Check if billing matches shop to set the checkbox
      if (existingLocations) {
        const existingShop = existingLocations.find(loc => loc.type === 0 && loc.active)
        if (existingShop) {
          const addressesMatch =
            existingShop.address1 === invoicingAddress.address1 &&
            existingShop.city === invoicingAddress.city &&
            existingShop.stateCode === invoicingAddress.state &&
            existingShop.zip === invoicingAddress.zip &&
            existingShop.countryCode === invoicingAddress.countryCode

          dispatch({ type: 'TOGGLE_BILLING_SAME_AS_SHOP', payload: addressesMatch })
        }
      }
    }
  }, [existingLocations, currentInvoicingSettings])

  // Geocode address to get coordinates
  const geocodeAddress = async (address: string): Promise<{ latitude: number; longitude: number } | null> => {
    const token = (window as any).env.MAPBOX_ACCESS_TOKEN
    const encoded = encodeURIComponent(address)
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${token}&limit=1`

    try {
      const response = await fetch(url)
      const data = await response.json()
      if (data.features && data.features.length > 0) {
        const [longitude, latitude] = data.features[0].center
        return { latitude, longitude }
      }
      return null
    } catch {
      return null
    }
  }

  // Handle Mapbox address selection for shop address
  const handleShopAddressRetrieve = (res: any) => {
    const context = res.properties?.context || {}

    // For street addresses, use address.name. For place-level results, fall back to the place name or full address
    const address1 = context.address?.name
      || res.properties?.address_line1
      || res.properties?.name
      || res.properties?.full_address?.split(',')[0]
      || ''
    const city = context.place?.name || res.properties?.context_city || res.properties?.name || ''
    const state = context.region?.region_code || res.properties?.context_region || ''
    const zip = context.postcode?.name || res.properties?.context_postcode || ''
    const county = context.district?.name || ''
    const countryCode = (context.country?.country_code || res.properties?.context_country || 'US').toUpperCase()
    const latitude = res.geometry?.coordinates?.[1] || null
    const longitude = res.geometry?.coordinates?.[0] || null

    console.log('[AccountSetup] Shop address retrieved:', { address1, city, state, zip, countryCode, latitude, longitude, rawResponse: res })

    // Update reducer state
    dispatch({
      type: 'UPDATE_SHOP_ADDRESS',
      payload: { address1, city, state, zip, county, countryCode, latitude, longitude }
    })

    // Update Ant Design form
    shopForm.setFieldsValue({ address1, city, state, zip, countryCode })

    // Update state dropdown options for new country
    setShopStateOptions(buildStateOptions(countryCode))
    setShopStateLabel(getStateLabel(countryCode))
  }

  // Handle Mapbox address selection for billing address
  const handleBillingAddressRetrieve = (res: any) => {
    const context = res.properties?.context || {}

    // For street addresses, use address.name. For place-level results, fall back to the place name or full address
    const address1 = context.address?.name
      || res.properties?.address_line1
      || res.properties?.name
      || res.properties?.full_address?.split(',')[0]
      || ''
    const city = context.place?.name || res.properties?.context_city || res.properties?.name || ''
    const state = context.region?.region_code || res.properties?.context_region || ''
    const zip = context.postcode?.name || res.properties?.context_postcode || ''
    const countryCode = (context.country?.country_code || res.properties?.context_country || 'US').toUpperCase()

    console.log('[AccountSetup] Billing address retrieved:', { address1, city, state, zip, countryCode, rawResponse: res })

    // Update reducer state
    dispatch({
      type: 'UPDATE_BILLING_ADDRESS',
      payload: { address1, city, state, zip, countryCode }
    })

    // Update Ant Design form
    billingForm.setFieldsValue({ address1, city, state, zip, countryCode })

    // Update state dropdown options for new country
    setBillingStateOptions(buildStateOptions(countryCode))
    setBillingStateLabel(getStateLabel(countryCode))
  }

  // Handle "Same as shop address" checkbox
  const handleSameAsShopChange = (e: any) => {
    const checked = e.target.checked
    dispatch({ type: 'TOGGLE_BILLING_SAME_AS_SHOP', payload: checked })

    if (checked) {
      // Copy shop address to billing form
      billingForm.setFieldsValue({
        address1: businessDetails.shopAddress.address1,
        city: businessDetails.shopAddress.city,
        state: businessDetails.shopAddress.state,
        zip: businessDetails.shopAddress.zip,
        countryCode: businessDetails.shopAddress.countryCode
      })
      setBillingStateOptions(shopStateOptions)
      setBillingStateLabel(shopStateLabel)
    }
  }

  // Save data to backend
  const handleFinish = async () => {
    try {
      // Validate forms
      await shopForm.validateFields()
      if (!businessDetails.billingAddressSameAsShop) {
        await billingForm.validateFields()
      }

      // Geocode shop address if coordinates missing
      let shopLat = businessDetails.shopAddress.latitude || 0
      let shopLng = businessDetails.shopAddress.longitude || 0

      if (!shopLat || !shopLng) {
        const fullAddress = `${businessDetails.shopAddress.address1}, ${businessDetails.shopAddress.city}, ${businessDetails.shopAddress.state} ${businessDetails.shopAddress.zip}`
        const coords = await geocodeAddress(fullAddress)
        if (coords) {
          shopLat = coords.latitude
          shopLng = coords.longitude
          // Update state with new coordinates
          dispatch({
            type: 'UPDATE_SHOP_ADDRESS',
            payload: { latitude: shopLat, longitude: shopLng }
          })
        }
      }

      // Check for existing shop location
      const existingShop = existingLocations?.find(loc => loc.type === 0 && loc.active)

      const shopRequest: CreateUpdateTenantLocationRequest = {
        type: 0, // Shop
        active: true,
        address1: businessDetails.shopAddress.address1,
        address2: null,
        county: businessDetails.shopAddress.county || '',
        city: businessDetails.shopAddress.city,
        stateCode: businessDetails.shopAddress.state,
        zip: businessDetails.shopAddress.zip,
        countryCode: businessDetails.shopAddress.countryCode,
        latitude: shopLat,
        longitude: shopLng
      }

      // Create or update shop location
      if (existingShop) {
        await updateTenantLocation.mutateAsync({ id: existingShop.id, request: shopRequest })
      } else {
        await createTenantLocation.mutateAsync(shopRequest)
      }

      // Update billing address in invoicing settings
      const billingAddress = businessDetails.billingAddressSameAsShop
        ? {
            address1: businessDetails.shopAddress.address1,
            city: businessDetails.shopAddress.city,
            state: businessDetails.shopAddress.state,
            zip: businessDetails.shopAddress.zip,
            countryCode: businessDetails.shopAddress.countryCode
          }
        : businessDetails.billingAddress

      await updateGeneralSettings.mutateAsync({
        invoicingAddress: billingAddress,
        supportEmail: currentInvoicingSettings?.supportEmail || '',
        defaultDueInDays: currentInvoicingSettings?.defaultDueInDays || 0
      })

      // Update Tenant.address field (shown in "My Company" page)
      if (tenantData) {
        const fullAddress = [
          businessDetails.shopAddress.address1,
          businessDetails.shopAddress.city,
          businessDetails.shopAddress.state,
          businessDetails.shopAddress.zip
        ].filter(Boolean).join(', ')

        await saveTenantMutation.mutateAsync({
          tenant: {
            ...tenantData,
            address: fullAddress
          },
          images: {}
        })
      }

      // Always advance to next step after mutations complete
      dispatch({ type: 'NEXT_STEP' })
    } catch (error) {
      console.error('Error saving addresses:', error)
      // Error notifications are handled by mutations
    }
  }

  // Form validation
  const isShopValid = !!(
    businessDetails.shopAddress.address1 &&
    businessDetails.shopAddress.city &&
    businessDetails.shopAddress.state &&
    businessDetails.shopAddress.zip &&
    businessDetails.shopAddress.countryCode
  )

  const isBillingValid = businessDetails.billingAddressSameAsShop || !!(
    businessDetails.billingAddress.address1 &&
    businessDetails.billingAddress.city &&
    businessDetails.billingAddress.state &&
    businessDetails.billingAddress.zip &&
    businessDetails.billingAddress.countryCode
  )

  const isFormValid = isShopValid && isBillingValid

  return (
    <div className="account-setup-modal-content">
      <div className="account-setup-progress-bar">
        <div className="account-setup-progress-fill" style={{ width: '25%' }} />
      </div>
      <button
        className="account-setup-exit-button"
        onClick={onExit}
        aria-label="Exit setup"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>

      <div className="account-setup-modal-left account-setup-form-container">
        <div className="account-setup-form-header">
          <div className="account-setup-modal-icon">
            <FontAwesomeIcon icon={faBuilding} size="2x" />
          </div>
          <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
            What's Your Shop and Billing Address?
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Enter the main shop address and your billing address.
          </Text>
        </div>

        <div className="account-setup-form">
          <Form.Item label="Shop Address" style={{ marginBottom: 8, fontWeight: 600 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              This is your main business location.
            </Text>
          </Form.Item>

          <Form
            form={shopForm}
            layout="vertical"
            onValuesChange={(changed) => {
              if (changed.countryCode) {
                setShopStateOptions(buildStateOptions(changed.countryCode))
                setShopStateLabel(getStateLabel(changed.countryCode))
                shopForm.setFieldValue('state', undefined)
              }
              dispatch({ type: 'UPDATE_SHOP_ADDRESS', payload: changed })
            }}
          >
            <Form.Item
              label="Street Address"
              required
              style={{ marginBottom: 12 }}
            >
              <Geocoder
                accessToken={(window as any).env.MAPBOX_ACCESS_TOKEN}
                placeholder="Search for your shop address"
                value={businessDetails.shopAddress.address1 || undefined}
                onRetrieve={handleShopAddressRetrieve}
                theme={{variables: {
                  borderRadius: '6px',
                  colorPrimary: '#FF5C35',
                  colorBackground: '#ffffff',
                  colorText: 'rgba(0, 0, 0, 0.88)',
                  colorSecondary: 'rgba(0, 0, 0, 0.45)',
                }}}
              />
            </Form.Item>

            <div style={{ display: 'flex', gap: 16 }}>
              <Form.Item
                label="Country"
                name="countryCode"
                rules={[{ required: true, message: 'Please select your country' }]}
                style={{ flex: 1 }}
              >
                <Select
                  placeholder="Select country"
                  size="large"
                  options={countryOptions}
                />
              </Form.Item>

              <Form.Item
                label="City"
                name="city"
                rules={[{ required: true, message: 'Please enter your city' }]}
                style={{ flex: 1 }}
              >
                <Input placeholder="City" size="large" />
              </Form.Item>
            </div>

            <div style={{ display: 'flex', gap: 16 }}>
              <Form.Item
                label={shopStateLabel}
                name="state"
                rules={[{ required: true, message: `Please enter your ${shopStateLabel.toLowerCase()}` }]}
                style={{ flex: 1 }}
              >
                {shopStateOptions.length > 0 ? (
                  <Select
                    placeholder={`Select ${shopStateLabel.toLowerCase()}`}
                    size="large"
                    options={shopStateOptions}
                  />
                ) : (
                  <Input placeholder={shopStateLabel} size="large" />
                )}
              </Form.Item>

              <Form.Item
                label="Zipcode"
                name="zip"
                rules={[{ required: true, message: 'Please enter your zipcode' }]}
                style={{ flex: 1 }}
              >
                <Input placeholder="00000" size="large" />
              </Form.Item>
            </div>
          </Form>

          {/* Billing Address Section */}
          <div style={{ marginTop: 32, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <Text strong style={{ fontSize: 14 }}>Billing Address</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  This appears on invoices and payment screens.
                </Text>
              </div>
              <Checkbox
                checked={businessDetails.billingAddressSameAsShop}
                onChange={handleSameAsShopChange}
              >
                Same as shop address
              </Checkbox>
            </div>

            {!businessDetails.billingAddressSameAsShop && (
              <Form
                form={billingForm}
                layout="vertical"
                onValuesChange={(changed) => {
                  if (changed.countryCode) {
                    setBillingStateOptions(buildStateOptions(changed.countryCode))
                    setBillingStateLabel(getStateLabel(changed.countryCode))
                    billingForm.setFieldValue('state', undefined)
                  }
                  dispatch({ type: 'UPDATE_BILLING_ADDRESS', payload: changed })
                }}
              >
              <Form.Item
                label="Street Address"
                required
                style={{ marginBottom: 12 }}
              >
                <Geocoder
                  accessToken={(window as any).env.MAPBOX_ACCESS_TOKEN}
                  placeholder="Search for your billing address"
                  value={businessDetails.billingAddress.address1 || undefined}
                  onRetrieve={handleBillingAddressRetrieve}
                  theme={{variables: {
                    borderRadius: '6px',
                    colorPrimary: '#FF5C35',
                    colorBackground: '#ffffff',
                    colorText: 'rgba(0, 0, 0, 0.88)',
                    colorSecondary: 'rgba(0, 0, 0, 0.45)',
                  }}}
                />
              </Form.Item>

              <div style={{ display: 'flex', gap: 16 }}>
                <Form.Item
                  label="Country"
                  name="countryCode"
                  rules={[{ required: true, message: 'Please select country' }]}
                  style={{ flex: 1 }}
                >
                  <Select
                    placeholder="Select country"
                    size="large"
                    options={countryOptions}
                  />
                </Form.Item>

                <Form.Item
                  label="City"
                  name="city"
                  rules={[{ required: true, message: 'Please enter city' }]}
                  style={{ flex: 1 }}
                >
                  <Input placeholder="City" size="large" />
                </Form.Item>
              </div>

              <div style={{ display: 'flex', gap: 16 }}>
                <Form.Item
                  label={billingStateLabel}
                  name="state"
                  rules={[{ required: true, message: `Please enter ${billingStateLabel.toLowerCase()}` }]}
                  style={{ flex: 1 }}
                >
                  {billingStateOptions.length > 0 ? (
                    <Select
                      placeholder={`Select ${billingStateLabel.toLowerCase()}`}
                      size="large"
                      options={billingStateOptions}
                    />
                  ) : (
                    <Input placeholder={billingStateLabel} size="large" />
                  )}
                </Form.Item>

                <Form.Item
                  label="Zipcode"
                  name="zip"
                  rules={[{ required: true, message: 'Please enter zipcode' }]}
                  style={{ flex: 1 }}
                >
                  <Input placeholder="00000" size="large" />
                </Form.Item>
              </div>
              </Form>
            )}
          </div>

          <div className="account-setup-form-buttons">
            <Button size="large" onClick={onBack} disabled={isSaving}>
              <FontAwesomeIcon icon={faChevronLeft} /> Back
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleFinish}
              loading={isSaving}
              disabled={!isFormValid || isSaving}
            >
              Next <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>
        </div>
      </div>

      <div className="account-setup-modal-right">
        <img
          src="/images/account-setup/step-2.png"
          alt="Shop and billing address"
          className="account-setup-modal-image"
        />
        <div className="account-setup-modal-logo">
          <img src="/images/synkedup-dark.png" alt="SynkedUP" />
        </div>
        <div className="account-setup-modal-testimonial-wrapper">
          <div className="account-setup-modal-testimonial">
            <Text className="account-setup-testimonial-quote">
              "I can turn around a $250k+ pool estimate in under 2 minutes"
            </Text>
          </div>
          <div className="account-setup-testimonial-author">
            <Text strong style={{ color: 'white' }}>Steve Kidwell</Text>
            <Text style={{ color: 'rgba(255,255,255,0.7)', display: 'block', fontSize: 14 }}>
              Prime Outdoor Living
            </Text>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccountSetupAddressStep

// Helper functions
const countryOptions = getAllCountries().map((country) => ({
  label: country.name,
  value: country.code
}))

const buildStateOptions = (countryCode: string) => {
  const states = getForCountry(countryCode) ?? []
  return states.map((state) => ({
    label: state.name,
    value: state.code
  }))
}

const getStateLabel = (countryCode: string) => {
  return getAllCountries().find(c => c.code === countryCode)?.stateLabel ?? 'State / Province / Region'
}
